<template>
  <div class="dashboard-container">
    <div class="dashboard-header">
      <h1>Головна панель</h1>
      <p>Ласкаво просимо, {{ currentUser?.first_name || 'Користувач' }}!</p>
    </div>

    <!-- Stats Cards -->
    <div class="stats-grid">
      <Card class="stat-card">
        <template #content>
          <div class="stat-content">
            <div class="stat-icon">
              <i class="pi pi-file"></i>
            </div>
            <div class="stat-info">
              <h3>{{ activeSessions.length }}</h3>
              <p>Активні тести</p>
            </div>
          </div>
        </template>
      </Card>

      <Card class="stat-card">
        <template #content>
          <div class="stat-content">
            <div class="stat-icon">
              <i class="pi pi-check-circle"></i>
            </div>
            <div class="stat-info">
              <h3>{{ completedSessions.length }}</h3>
              <p>Завершені тести</p>
            </div>
          </div>
        </template>
      </Card>

      <Card class="stat-card">
        <template #content>
          <div class="stat-content">
            <div class="stat-icon">
              <i class="pi pi-chart-bar"></i>
            </div>
            <div class="stat-info">
              <h3>{{ results.length }}</h3>
              <p>Ваші результати</p>
            </div>
          </div>
        </template>
      </Card>

      <Card class="stat-card">
        <template #content>
          <div class="stat-content">
            <div class="stat-icon">
              <i class="pi pi-users"></i>
            </div>
            <div class="stat-info">
              <h3>{{ availableTests.length }}</h3>
              <p>Доступні тести</p>
            </div>
          </div>
        </template>
      </Card>
    </div>

    <!-- Recent Activity -->
    <div class="dashboard-sections">
      <div class="section">
        <Card>
          <template #title>
            <div class="section-header">
              <h2>Активні тести</h2>
              <Button 
                label="Переглянути всі" 
                icon="pi pi-arrow-right" 
                text 
                @click="$router.push('/tests')"
              />
            </div>
          </template>
          
          <template #content>
            <div v-if="activeSessions.length === 0" class="empty-state">
              <i class="pi pi-inbox"></i>
              <p>Немає активних тестів</p>
            </div>
            
            <div v-else class="session-list">
              <div 
                v-for="session in activeSessions.slice(0, 3)" 
                :key="session.id" 
                class="session-item"
                @click="$router.push(`/test/${session.id}`)"
              >
                <div class="session-info">
                  <div class="session-name-container">
                    <h4>{{ session.name }}</h4>
                    <Tag 
                      v-if="session.is_anonymous" 
                      value="Анонімні відповіді" 
                      severity="info" 
                      class="anonymous-badge"
                    />
                  </div>
                  <p>{{ session.test_type_name }}</p>
                  <small>До: {{ formatDate(session.end_date) }}</small>
                </div>
                <div class="session-status">
                  <Tag value="Активний" severity="success" />
                </div>
              </div>
            </div>
          </template>
        </Card>
      </div>

      <div class="section">
        <Card>
          <template #title>
            <div class="section-header">
              <h2>Останні результати</h2>
              <Button 
                label="Переглянути всі" 
                icon="pi pi-arrow-right" 
                text 
                @click="$router.push('/results')"
              />
            </div>
          </template>
          
          <template #content>
            <div v-if="results.length === 0" class="empty-state">
              <i class="pi pi-chart-bar"></i>
              <p>Немає результатів</p>
            </div>
            
            <div v-else class="results-list">
              <div 
                v-for="result in results.slice(0, 3)" 
                :key="result.id" 
                class="result-item"
              >
                <div class="result-info">
                  <h4>{{ result.session_name }}</h4>
                  <p>Середній бал: {{ result.average_score || 'N/A' }}</p>
                  <small>{{ formatDate(result.completed_at) }}</small>
                </div>
                <div class="result-score">
                  <Tag :value="result.average_score || 'N/A'" severity="primary" />
                </div>
              </div>
            </div>
          </template>
        </Card>
      </div>
    </div>

    <!-- Admin Section -->
    <div v-if="isAdmin" class="admin-section">
      <Card>
        <template #title>
          <h2>Адміністративні дії</h2>
        </template>
        
        <template #content>
          <div class="admin-actions">
            <Button 
              label="Створити тест" 
              icon="pi pi-plus" 
              @click="$router.push('/admin')"
              class="admin-btn"
            />
            <Button 
              label="Управління користувачами" 
              icon="pi pi-users" 
              @click="$router.push('/admin')"
              class="admin-btn"
              severity="secondary"
            />
            <Button 
              label="Аналітика" 
              icon="pi pi-chart-line" 
              @click="$router.push('/admin')"
              class="admin-btn"
              severity="info"
            />
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import moment from 'moment'

export default {
  name: 'Dashboard',
  computed: {
    ...mapState(['currentUser', 'sessions', 'results']),
    ...mapGetters(['isAdmin', 'availableTests', 'activeSessions', 'completedSessions'])
  },
  methods: {
    ...mapActions(['fetchSessionsWithCache', 'fetchResultsWithCache']),
    
    formatDate(date) {
      return moment(date).format('DD.MM.YYYY HH:mm')
    }
  },
  async mounted() {
    await Promise.all([
      this.fetchSessionsWithCache(),
      this.fetchResultsWithCache()
    ])
  }
}
</script>

<style scoped>
.dashboard-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.dashboard-header {
  margin-bottom: 2rem;
}

.dashboard-header h1 {
  margin: 0 0 0.5rem 0;
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-color);
}

.dashboard-header p {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 1.1rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  transition: transform 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-600) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.stat-info h3 {
  margin: 0 0 0.25rem 0;
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-color);
}

.stat-info p {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
}

.dashboard-sections {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: var(--text-color-secondary);
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  display: block;
  opacity: 0.5;
}

.session-list, .results-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.session-item, .result-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.session-item:hover, .result-item:hover {
  background: var(--surface-hover);
  border-color: var(--primary-color);
}

.session-info h4, .result-info h4 {
  margin: 0 0 0.25rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.session-info p, .result-info p {
  margin: 0 0 0.25rem 0;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
}

.session-info small, .result-info small {
  color: var(--text-color-secondary);
  font-size: 0.8rem;
}

.session-name-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.anonymous-badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.admin-section {
  margin-top: 2rem;
}

.admin-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.admin-btn {
  flex: 1;
  min-width: 200px;
}

@media (max-width: 768px) {
  .dashboard-sections {
    grid-template-columns: 1fr;
  }
  
  .stats-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
  
  .admin-actions {
    flex-direction: column;
  }
  
  .admin-btn {
    min-width: auto;
  }
}
</style> 